/**
 * AuthSplitLayout — shared split-screen auth shell (iter222b/c).
 * Left: dark brand panel (WebGL rotating dotted globe via `cobe`, themed logo,
 * live member count). Right: clean form panel. Accent tone shifts per portal:
 *   hacker=cyan · recruiter=violet · company=emerald · admin=amber
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BrandMark from "./BrandMark";
import { LAND_DOTS } from "./globeDots";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const NOISE = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const TONES = {
    cyan:    { box: "bg-cyan-400",    dot: "text-cyan-400",    glow: "rgba(6,182,212,0.20)",  link: "text-cyan-300 hover:text-cyan-200",       rgb: "34,211,238",  markerRgb: "165,243,252" },
    violet:  { box: "bg-violet-400",  dot: "text-violet-300",  glow: "rgba(139,92,246,0.22)", link: "text-violet-300 hover:text-violet-200",   rgb: "167,139,250", markerRgb: "221,214,254" },
    emerald: { box: "bg-emerald-400", dot: "text-emerald-300", glow: "rgba(16,185,129,0.20)", link: "text-emerald-300 hover:text-emerald-200", rgb: "52,211,153",  markerRgb: "167,243,208" },
    amber:   { box: "bg-amber-400",   dot: "text-amber-300",   glow: "rgba(251,191,36,0.18)", link: "text-amber-300 hover:text-amber-200",     rgb: "251,191,36",  markerRgb: "253,230,138" },
};

// Global hacker-community hubs
const HUBS = [
    { name: "San Francisco", lat: 37.77, lng: -122.42 }, { name: "New York", lat: 40.71, lng: -74.0 },
    { name: "London", lat: 51.5, lng: -0.12 }, { name: "Berlin", lat: 52.52, lng: 13.4 },
    { name: "Delhi", lat: 28.61, lng: 77.2 }, { name: "Bengaluru", lat: 12.97, lng: 77.59 },
    { name: "Tokyo", lat: 35.68, lng: 139.69 }, { name: "Singapore", lat: 1.35, lng: 103.82 },
    { name: "São Paulo", lat: -23.55, lng: -46.63 }, { name: "Moscow", lat: 55.75, lng: 37.62 },
    { name: "Shanghai", lat: 31.23, lng: 121.47 }, { name: "Sydney", lat: -33.87, lng: 151.21 },
    { name: "Cairo", lat: 30.04, lng: 31.24 }, { name: "Mexico City", lat: 19.43, lng: -99.13 },
];

const D2R = Math.PI / 180;
function toVec(lat, lng) {
    const la = lat * D2R, lo = lng * D2R;
    return [Math.cos(la) * Math.sin(lo), Math.sin(la), Math.cos(la) * Math.cos(lo)];
}

// Map a signup to a hub city: real location match first, else deterministic by username.
function cityFor(signup) {
    const loc = (signup.location || "").toLowerCase();
    if (loc) {
        const hit = HUBS.find((h) => loc.includes(h.name.toLowerCase()));
        if (hit) return hit;
    }
    let h = 0;
    const s = signup.username || "";
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return HUBS[h % HUBS.length];
}

/** In-house rotating dotted globe — pure Canvas2D, themed per portal, drag-to-spin. */
function RotatingGlobe({ tone, burstRef }) {
    const canvasRef = useRef(null);
    const drag = useRef({ active: false, lastX: 0, vel: 0 });
    const phiRef = useRef(4.6);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext("2d");
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        const n = LAND_DOTS.length / 2;
        const vx = new Float32Array(n), vy = new Float32Array(n), vz = new Float32Array(n);
        for (let i = 0; i < n; i++) {
            const [x, y, z] = toVec(LAND_DOTS[i * 2], LAND_DOTS[i * 2 + 1]);
            vx[i] = x; vy[i] = y; vz[i] = z;
        }
        const hubs = HUBS.map((h) => toVec(h.lat, h.lng));

        let size = 0;
        const fit = () => {
            size = canvas.clientWidth;
            canvas.width = size * dpr;
            canvas.height = size * dpr;
        };
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(canvas);

        const theta = 0.32, sinT = Math.sin(theta), cosT = Math.cos(theta);
        const LEVELS = 6;
        let raf = 0;

        const render = (t) => {
            const d = drag.current;
            const burst = burstRef?.current;
            if (!d.active) {
                // swing toward a fresh signup burst city, then resume idle spin
                if (burst && t - burst.start < 1200) {
                    let diff = burst.targetPhi - phiRef.current;
                    diff = (((diff + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
                    phiRef.current += diff * 0.08;
                } else if (!reduced) {
                    phiRef.current += 0.0045 + d.vel;
                }
                d.vel *= 0.95;
            }
            const P = phiRef.current;
            const sinP = Math.sin(P), cosP = Math.cos(P);
            const px = size * dpr, cx = px / 2, cy = px / 2, R = px * 0.47;
            ctx.clearRect(0, 0, px, px);

            // sphere body + soft top-light
            const body = ctx.createRadialGradient(cx - R * 0.35, cy - R * 0.45, R * 0.1, cx, cy, R);
            body.addColorStop(0, `rgba(${tone.rgb},0.10)`);
            body.addColorStop(0.55, "rgba(10,16,22,0.85)");
            body.addColorStop(1, "rgba(4,7,10,0.95)");
            ctx.beginPath(); ctx.arc(cx, cy, R, 0, 7); ctx.fillStyle = body; ctx.fill();

            // land dots, batched into brightness buckets
            const paths = Array.from({ length: LEVELS }, () => new Path2D());
            const baseDot = Math.max(1, R / 165);
            for (let i = 0; i < n; i++) {
                const x1 = vx[i] * cosP + vz[i] * sinP;
                const z1 = -vx[i] * sinP + vz[i] * cosP;
                const y2 = vy[i] * cosT - z1 * sinT;
                const z2 = vy[i] * sinT + z1 * cosT;
                if (z2 < 0.03) continue;
                const b = Math.min(LEVELS - 1, Math.floor(z2 * LEVELS));
                const r = baseDot * (0.55 + z2 * 0.75);
                const sx = cx + x1 * R, sy = cy - y2 * R;
                paths[b].moveTo(sx + r, sy);
                paths[b].arc(sx, sy, r, 0, 7);
            }
            for (let b = 0; b < LEVELS; b++) {
                ctx.fillStyle = `rgba(${tone.rgb},${(0.16 + (b / (LEVELS - 1)) * 0.66).toFixed(3)})`;
                ctx.fill(paths[b]);
            }

            // hub markers with pulse rings
            for (let i = 0; i < hubs.length; i++) {
                const [hx, hy, hz] = hubs[i];
                const x1 = hx * cosP + hz * sinP;
                const z1 = -hx * sinP + hz * cosP;
                const y2 = hy * cosT - z1 * sinT;
                const z2 = hy * sinT + z1 * cosT;
                if (z2 < 0.08) continue;
                const sx = cx + x1 * R, sy = cy - y2 * R;
                const mr = baseDot * 1.7 * (0.6 + z2 * 0.6);
                const pulse = ((t / 1600) + i * 0.37) % 1;
                ctx.beginPath(); ctx.arc(sx, sy, mr * (1 + pulse * 2.4), 0, 7);
                ctx.strokeStyle = `rgba(${tone.markerRgb},${(0.5 * (1 - pulse) * z2).toFixed(3)})`;
                ctx.lineWidth = dpr; ctx.stroke();
                ctx.beginPath(); ctx.arc(sx, sy, mr, 0, 7);
                ctx.fillStyle = `rgba(${tone.markerRgb},${(0.35 + z2 * 0.6).toFixed(3)})`;
                ctx.fill();
            }

            // live signup burst — expanding ring at the signup city
            if (burst) {
                const age = t - burst.start;
                if (age < 2600) {
                    const [bx, by, bz] = burst.vec;
                    const x1 = bx * cosP + bz * sinP;
                    const z1 = -bx * sinP + bz * cosP;
                    const y2 = by * cosT - z1 * sinT;
                    const z2 = by * sinT + z1 * cosT;
                    if (z2 > 0.05) {
                        const sx = cx + x1 * R, sy = cy - y2 * R;
                        const k = age / 2600;
                        ctx.beginPath(); ctx.arc(sx, sy, baseDot * (2 + k * 16), 0, 7);
                        ctx.strokeStyle = `rgba(${tone.markerRgb},${(0.9 * (1 - k)).toFixed(3)})`;
                        ctx.lineWidth = dpr * 1.5; ctx.stroke();
                        ctx.beginPath(); ctx.arc(sx, sy, baseDot * 2.2, 0, 7);
                        ctx.fillStyle = `rgba(${tone.markerRgb},${(0.95 * (1 - k * 0.4)).toFixed(3)})`;
                        ctx.fill();
                    }
                }
            }

            // rim + outer atmosphere glow
            const rim = ctx.createRadialGradient(cx, cy, R * 0.88, cx, cy, R * 1.06);
            rim.addColorStop(0, "rgba(0,0,0,0)");
            rim.addColorStop(0.82, `rgba(${tone.rgb},0.22)`);
            rim.addColorStop(0.92, `rgba(${tone.rgb},0.10)`);
            rim.addColorStop(1, "rgba(0,0,0,0)");
            ctx.beginPath(); ctx.arc(cx, cy, R * 1.06, 0, 7); ctx.fillStyle = rim; ctx.fill();

            raf = requestAnimationFrame(render);
        };
        raf = requestAnimationFrame(render);
        return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    }, [tone]);

    const onDown = (e) => {
        drag.current = { active: true, lastX: e.clientX, vel: 0 };
        e.currentTarget.setPointerCapture?.(e.pointerId);
        e.currentTarget.style.cursor = "grabbing";
    };
    const onMove = (e) => {
        const d = drag.current;
        if (!d.active) return;
        const dx = e.clientX - d.lastX;
        d.lastX = e.clientX;
        phiRef.current += dx / 220;
        d.vel = Math.max(-0.06, Math.min(0.06, dx * 0.00045 + d.vel * 0.5));
    };
    const onUp = (e) => { drag.current.active = false; e.currentTarget.style.cursor = "grab"; };

    return (
        <canvas
            ref={canvasRef}
            className="absolute left-1/2"
            style={{ width: "min(92%, 760px)", aspectRatio: "1 / 1", height: "auto", top: "46%", transform: "translate(-50%, -50%)", cursor: "grab", touchAction: "none" }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerLeave={onUp}
            aria-hidden="true"
            data-testid="auth-globe-canvas"
        />
    );
}

function Brand({ tone, small = false }) {
    return <BrandMark tone={tone} size={small ? "sm" : "md"} tagline="play in the sandbox." />;
}

export default function AuthSplitLayout({ tone = "cyan", panelSub, children, testid }) {
    const t = TONES[tone] || TONES.cyan;
    const [users, setUsers] = useState(null);
    useEffect(() => {
        let cancel = false;
        axios.get(`${API}/landing/stats`).then(({ data }) => { if (!cancel) setUsers(data?.users); }).catch(() => {});
        return () => { cancel = true; };
    }, []);
    const memberLine = users ? `Home to ${Number(users).toLocaleString()} ethical hackers worldwide` : "The home for ethical hackers worldwide";

    // Live signup feed → globe bursts + social-proof chip
    const [feed, setFeed] = useState(null);
    const burstRef = useRef(null);
    useEffect(() => {
        let timer, idx = 0, cancel = false;
        axios.get(`${API}/landing/recent-signups`).then(({ data }) => {
            if (cancel) return;
            const list = data?.signups || [];
            if (!list.length) return;
            const fire = () => {
                const s = list[idx % list.length]; idx += 1;
                const city = cityFor(s);
                burstRef.current = { vec: toVec(city.lat, city.lng), targetPhi: -city.lng * D2R, start: performance.now() };
                setFeed({ username: s.username, city: city.name, key: idx });
            };
            fire();
            timer = setInterval(fire, 6500);
        }).catch(() => {});
        return () => { cancel = true; clearInterval(timer); };
    }, []);

    return (
        <div className="min-h-screen bg-[#05070a] text-slate-200 lg:grid lg:grid-cols-[1.1fr_1fr]" data-testid={testid}>
            <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-cyan-400 focus:text-slate-950 focus:text-xs focus:font-bold focus:px-3 focus:py-1.5 focus:rounded-sm">skip to main content</a>
            {/* ── Left brand panel ── */}
            <div className="relative hidden lg:flex flex-col justify-between overflow-hidden p-10 xl:p-14" aria-hidden="true">
                <RotatingGlobe tone={t} burstRef={burstRef} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05070a] via-transparent to-[#05070a]/70 pointer-events-none" />
                <div className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full blur-[130px] pointer-events-none" style={{ background: t.glow }} />
                <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{ backgroundImage: NOISE }} />

                <Link to="/" className="relative flex items-center gap-3 w-fit" data-testid="auth-brand-link">
                    <Brand tone={tone} />
                </Link>

                <div className="relative max-w-md">
                    {feed && (
                        <div key={feed.key} data-testid="auth-live-signup-chip"
                             className="mb-5 inline-flex items-center gap-2 rounded-sm border border-white/10 bg-white/[0.04] backdrop-blur px-3 py-1.5 font-mono text-[11px] text-slate-300"
                             style={{ animation: "hcAuthFeed .6s ease" }}>
                            <span className={`w-1.5 h-1.5 rounded-full ${t.box} animate-pulse`} />
                            <span className="text-white font-bold">{feed.username}</span>
                            <span>just joined · {feed.city}</span>
                        </div>
                    )}
                    <style>{"@keyframes hcAuthFeed { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }"}</style>
                    <div className="text-slate-400 text-lg">Welcome to</div>
                    <div className="font-black tracking-tighter text-white text-4xl xl:text-5xl mt-1" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                        hacking<span className={t.dot}>.</span>community
                    </div>
                    <p className="text-slate-400 mt-4 text-sm leading-relaxed">{panelSub || memberLine}</p>
                    {panelSub && <p className="text-slate-500 mt-1.5 text-xs font-mono">{memberLine.toLowerCase()}</p>}
                    <Link to="/" className={`inline-block mt-3 text-sm ${t.link}`} data-testid="auth-know-more">Know more →</Link>
                </div>
            </div>

            {/* ── Right form panel ── */}
            <div className="relative flex flex-col min-h-screen bg-[#070b12]">
                <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none" style={{ backgroundImage: NOISE }} aria-hidden="true" />
                <div className="lg:hidden relative px-6 pt-8">
                    <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="auth-brand-link-mobile">
                        <Brand tone={tone} small />
                    </Link>
                </div>
                <div className="relative flex-1 flex items-center justify-center px-5 sm:px-10 py-10">
                    <main id="main-content" tabIndex={-1} className="w-full max-w-md focus:outline-none">{children}</main>
                </div>
            </div>
        </div>
    );
}
